import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { isRestaurantClosed } from 'restaurantAdmin/reservations/apiHelpers';
import {
  clearSelectedAvailability,
  clearSelectedTime,
  createAvailabilityReducer,
  initialAvailabilityState,
  setAvailabilities,
  setSelectedAvailability,
  setSelectedDate,
  setSelectedFloorPlanListingIds,
  setSelectedFloorPlanTableIds,
  setSelectedGuestCount,
  setSelectedTime,
  toggleSelectedFloorPlanTableId,
} from '@shared/availability/availabilityReducer';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { useError } from '@shared/hooks/useError';
import type {
  FloorPlanData,
  FloorPlanTableData,
} from '@shared/types/floorPlans';
import { todayInTimezone, toISODateFormat } from '@shared/utils/dateFormatters';
import { isValid, parse } from 'date-fns';
import { zonedDateTime } from '@shared/utils/dateParsers';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useEffect, useMemo, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminFloorPlans } from '../../hooks/useAdminFloorPlans';
import type { AdminAvailabilityData } from './apiHelpers';
import { getAvailabilities } from './apiHelpers';
import {
  calculateFilteredData,
  getAvailabilitiesGroupedByTime,
} from './conciergeAvailabilityDerivedState';

export interface ConciergeAvailabilityContextState {
  clearSelectedAvailability: () => void;
  clearSelectedTime: () => void;
  expandedAvailabilityListingIds: string[];
  filteredAvailabilities: AdminAvailabilityData[];
  filteredAvailabilitiesGroupedByTime: Record<string, AdminAvailabilityData[]>;
  hasAvailabilitiesOnGivenDay: boolean;
  hasUnsupportedGuestCount: boolean;
  isClosedToday: boolean;
  isLoading: boolean;
  isSelectedDateToday: boolean;
  selectedAvailability: AdminAvailabilityData | null;
  selectedDate: Date;
  selectedFloorPlanListingIds: string[];
  selectedFloorPlanTableIds: string[];
  selectedFloorPlanTables: FloorPlanTableData[];
  selectedGuestCount: number;
  selectedTime: string | null;
  setSelectedAvailability: (availability: AdminAvailabilityData) => void;
  setSelectedDate: (date: Date) => void;
  setSelectedFloorPlanListingIds: (ids: string[]) => void;
  setSelectedFloorPlanTableIds: (id: string[]) => void;
  setSelectedGuestCount: (guestCount: number) => void;
  setSelectedTime: (time: string) => void;
  selectedFloorPlan: FloorPlanData;
  setSelectedFloorPlanId: Dispatch<SetStateAction<string>>;
  toggleSelectedFloorPlanTableId: (id: string) => void;
  floorPlans: FloorPlanData[];
  clearSelectedFloorPlanTables: () => void;
  multiTableMode: boolean;
}

export const ConciergeAvailabilityContext =
  createContext<ConciergeAvailabilityContextState | null>(null);
ConciergeAvailabilityContext.displayName = 'ConciergeAvailabilityContext';

export const useConciergeAvailabilityContext = () =>
  useDefinedContext(ConciergeAvailabilityContext);

interface ConciergeAvailabilityContextProviderProps {
  children: ReactNode;
  multiTableMode?: boolean;
}

export const ConciergeAvailabilityContextProvider = ({
  children,
  multiTableMode = false,
}: ConciergeAvailabilityContextProviderProps) => {
  const restaurant = useRestaurant();
  const [isClosedToday, setIsClosedToday] = useState(true);
  const conciergeAvailabilityReducer = createAvailabilityReducer<
    AdminAvailabilityData,
    AdminAvailabilityData
  >();

  const [searchParams] = useSearchParams();
  const dateSearchParam = searchParams.get('date');
  const parsedDateFromParam = dateSearchParam
    ? parse(dateSearchParam, 'yyyy-MM-dd', new Date())
    : null;
  const selectedDateFromSearchParams =
    dateSearchParam && parsedDateFromParam && isValid(parsedDateFromParam)
      ? zonedDateTime(dateSearchParam, '00:00:00', restaurant.timezone)
      : undefined;

  const [state, dispatch] = useReducer(
    conciergeAvailabilityReducer,
    initialAvailabilityState<AdminAvailabilityData, AdminAvailabilityData>(
      restaurant.timezone,
      selectedDateFromSearchParams,
    ),
  );
  const {
    availabilities,
    selectedGuestCount,
    selectedTime,
    selectedDate,
    selectedFloorPlanListingIds,
    selectedFloorPlanTableIds,
    selectedAvailability,
  } = state;

  const setError = useError();

  useEffect(() => {
    if (restaurant.id) {
      const checkRestaurantIsClosed = async () => {
        try {
          const isClosed = await isRestaurantClosed(
            restaurant.id,
            todayInTimezone(restaurant.timezone),
          );

          setIsClosedToday(isClosed);
        } catch (e) {
          setError(e);
        }
      };

      void checkRestaurantIsClosed();
    }
  }, [restaurant.id]);

  useEffect(() => {
    if (selectedDateFromSearchParams) {
      dispatch(setSelectedDate(selectedDateFromSearchParams));
    }
  }, [dateSearchParam]);

  const { isPending: isAvailabilitiesLoading } = useAbortEffect(async () => {
    if (restaurant.id) {
      // clear availabilities when fetch is in progress
      // prevent user from choosing an availability while fetching
      dispatch(setAvailabilities([]));
      const editingReservation =
        searchParams.has('occupantId') &&
        searchParams.get('occupantType') === 'reservation';

      const allAvailabilities = await getAvailabilities(
        restaurant.id,
        selectedDate,
        selectedGuestCount,
        editingReservation ? searchParams.get('occupantId')! : undefined,
      );
      void dispatch(setAvailabilities(allAvailabilities));
    }
  }, [selectedDate.toDateString(), selectedGuestCount, restaurant.id]);

  const {
    floorPlans,
    isLoading: isFloorPlanLoading,
    setSelectedFloorPlanId,
    selectedFloorPlan,
  } = useAdminFloorPlans({
    ...(!multiTableMode ? { date: selectedDate } : {}),
  });

  const {
    filteredAvailabilities,
    expandedAvailabilityListingIds,
    hasUnsupportedGuestCount,
  } = calculateFilteredData(state);

  const filteredAvailabilitiesGroupedByTime =
    getAvailabilitiesGroupedByTime(state);

  const isSelectedDateToday =
    todayInTimezone(restaurant.timezone) === toISODateFormat(selectedDate);

  const selectedFloorPlanTables = selectedFloorPlan?.floorPlanTables?.filter(
    (table) => selectedFloorPlanTableIds.includes(table.id),
  );

  const autoSelectBestAvailableAvailability = (time?: string) => {
    const availabilityTime = time || selectedTime;
    const bestAvailable = availabilities.find(
      (availability) =>
        availability.time === availabilityTime && availability.isBestAvailable,
    );
    if (bestAvailable) {
      dispatch(
        setSelectedAvailability({
          guestCount: bestAvailable.guestCount,
          isBiddable: bestAvailable.isBiddable,
          isBuyable: bestAvailable.isBuyable,
          listing: bestAvailable.listing,
          purchasePrice: bestAvailable.purchasePrice,
          serviceWindow: bestAvailable.serviceWindow,
          time: bestAvailable.time,
          date: bestAvailable.date,
          isBestAvailable: bestAvailable.isBestAvailable,
          isFeaturedUpgrade: bestAvailable.isFeaturedUpgrade,
          flaggedWhenSelected: true,
        }),
      );
    }
  };

  const handleTimeSelection = (time: string) => {
    dispatch(setSelectedTime(time));
    autoSelectBestAvailableAvailability(time);
  };

  const handleClickClearSelectedFloorPlanTables = () => {
    dispatch(setSelectedFloorPlanTableIds([]));
    dispatch(setSelectedFloorPlanListingIds([]));
    autoSelectBestAvailableAvailability();
  };

  const value = useMemo(
    (): ConciergeAvailabilityContextState => ({
      clearSelectedAvailability: () => dispatch(clearSelectedAvailability()),
      clearSelectedTime: () => dispatch(clearSelectedTime()),
      expandedAvailabilityListingIds,
      filteredAvailabilities,
      filteredAvailabilitiesGroupedByTime,
      floorPlans,
      hasAvailabilitiesOnGivenDay: !!availabilities.length,
      hasUnsupportedGuestCount,
      isClosedToday,
      isLoading: isAvailabilitiesLoading || isFloorPlanLoading,
      isSelectedDateToday,
      selectedAvailability,
      selectedDate,
      selectedFloorPlan,
      selectedGuestCount,
      selectedFloorPlanListingIds,
      selectedFloorPlanTableIds,
      selectedFloorPlanTables,
      selectedTime,
      setSelectedAvailability: (availability: AdminAvailabilityData) =>
        dispatch(setSelectedAvailability(availability)),
      setSelectedDate: (date: Date) => {
        dispatch(setSelectedDate(date));
      },
      setSelectedFloorPlanId,
      setSelectedFloorPlanListingIds: (floorPlanListingIds: string[]) =>
        dispatch(setSelectedFloorPlanListingIds(floorPlanListingIds)),
      setSelectedFloorPlanTableIds: (tableIds: string[]) =>
        dispatch(setSelectedFloorPlanTableIds(tableIds)),
      setSelectedGuestCount: (guestCount: number) =>
        dispatch(setSelectedGuestCount(guestCount)),
      setSelectedTime: handleTimeSelection,
      toggleSelectedFloorPlanTableId: (id: string) => {
        dispatch(toggleSelectedFloorPlanTableId(id));
      },
      clearSelectedFloorPlanTables: () => {
        handleClickClearSelectedFloorPlanTables();
      },
      multiTableMode,
    }),
    [
      isClosedToday,
      isAvailabilitiesLoading,
      isFloorPlanLoading,
      multiTableMode,
      state,
      floorPlans,
      selectedFloorPlan,
    ],
  );

  return (
    <ConciergeAvailabilityContext.Provider value={value}>
      {children}
    </ConciergeAvailabilityContext.Provider>
  );
};
